// src/api/authService.js
// -----------------------------------------------------------------------------
// All authentication related API calls. This file only talks to the real
// backend - it has no knowledge of the "demo account" shortcut; that lives in
// AuthContext.jsx (loginDemo) so it can set context/localStorage the same way
// a real login does, keeping ProtectedRoute / Header / Sidebar all working.
// -----------------------------------------------------------------------------
import axiosClient from './axiosClient';
import ENDPOINTS from './endpoints';

const authService = {
  /**
   * Logs the user in with email + password.
   * Real backend (Afix.Api AuthController.Login) returns HTTP 200 with either:
   *   - a plain string body on failure: "Invalid Email" | "Invalid Password"
   *   - { Message, UserId, Token, First_Name, Email, Phone_1, Role, IsPasswordupdated } on success
   *   - { ForcePasswordChange: true, UserId, Message } if the password must be changed
   * See AuthContext.login() for how this is parsed/normalized.
   */
  login: (email, password) => {
    return axiosClient.post(ENDPOINTS.AUTH_LOGIN, { email, password });
  },

  /** Sends a temporary password to the given email (Afix.Api: POST /api/Auth/forgot-password). */
  forgotPassword: (email) => {
    return axiosClient.post(ENDPOINTS.AUTH_FORGOT_PASSWORD, { email });
  },

  /** Registers a new user. payload matching STEP 3 payload schema. */
  register: (payload) => {
    return axiosClient.post(ENDPOINTS.AUTH_REGISTER, payload);
  },

  /** Changes the user password. (Afix.Api: POST /api/Auth/change-password). */
  changePassword: (email, oldPassword, newPassword) => {
    return axiosClient.post(ENDPOINTS.AUTH_CHANGE_PASSWORD, { email, oldPassword, newPassword });
  },

  /** Sets a new password using the one-time token from the emailed reset link (Afix.Api ResetPasswordDTO: { Token, NewPassword }). */
  resetPassword: (token, newPassword) => {
    return axiosClient.post(ENDPOINTS.AUTH_RESET_PASSWORD, { token, newPassword });
  },

  /** Exchanges a refresh token for a new access + refresh token (POST /api/Auth/refresh-token). */
  refreshToken: (refreshToken) => {
    return axiosClient.post(ENDPOINTS.AUTH_REFRESH, { refreshToken });
  },

  /** The logged-in user's own profile (GET /api/Auth/me). */
  getMyProfile: () => axiosClient.get(ENDPOINTS.AUTH_ME),

  /** Updates own name / phone. payload: { first_Name, last_Name, phone } */
  updateMyProfile: (payload) => axiosClient.put(ENDPOINTS.AUTH_ME, payload),

  /** Invalidates the session on the backend (optional, best-effort). */
  logout: (token) => {
    return axiosClient.post(
      ENDPOINTS.AUTH_LOGOUT,
      null,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
  },
};

export default authService;